import Cookies from "js-cookie";
import { getJwt } from "../../../utils/authHeaders";
import { fetchBullionLobbyMatchesCached, peekBullionLobbyMatchesCache } from "./bullionLobbyApi";
import { fetchBullionFixtureFrozenOdds, mergeBullionFrozenOddsOntoLobbyRow } from "./bullionFixtureFrozenOdds";
import { bullionOddsSnapshotMarkets } from "./bullionOddsSnapshot";
import { readCachedBullionLobbyEvents, writeCachedBullionLobbyEvents } from "./bullionLobbyEventsSessionCache";
import { mergeBullionLobbySourceLists } from "./bullionLobbyOrdering";
import { resolveBullionMatchFixtureState } from "./bullionFixturePhaseResolve";
import { peekBullionMyGamesByMatchIdCache } from "./bullionMyGamesByMatchIdCache";
import { fetchTraderCricketEventsCached } from "./bullionTraderCricketEventsCache";
import { stampFinishedOntoRawMatchRow } from "./bullionRawMatch";
import type { RawMatch } from "./bullionRawMatch";
import { buildBullionRooms } from "./roomUtils";
import type { BullionLobbyUserActiveGame, BullionPublicGame, BullionRoom } from "./types";

export type BullionOpenPlayGame = Pick<BullionPublicGame, "gameId" | "matchId"> & {
    inviteCode?: string | null;
};

export function normalizeBullionMatchQueryParam(raw: string): string {
    const t = raw.trim();
    try {
        return decodeURIComponent(t).trim();
    } catch {
        return t;
    }
}

/** Match ids from lobby rows vs `/bullion/my-games` can differ by encoding — compare canonically. */
export function bullionLobbyMatchIdsEqual(a: string, b: string): boolean {
    const na = normalizeBullionMatchQueryParam(a);
    const nb = normalizeBullionMatchQueryParam(b);
    return Boolean(na && nb && na === nb);
}

export function bullionLobbyUserHasActivePlay(matchId: string, userActiveMatchIds: Set<string>): boolean {
    const want = normalizeBullionMatchQueryParam(matchId);
    if (!want || userActiveMatchIds.size === 0) return false;
    for (const key of userActiveMatchIds) {
        if (normalizeBullionMatchQueryParam(key) === want) return true;
    }
    return false;
}

/** `location.state.room` from lobby PLAY — survives when the frozen lobby refetch omits this fixture. */
export function bullionStateRoomMatchesMatchQuery(
    room: BullionRoom | null | undefined,
    matchQuery: string
): boolean {
    if (!room || !String(matchQuery || "").trim()) return false;
    const want = normalizeBullionMatchQueryParam(matchQuery);
    if (!want) return false;
    const mid = String(room.matchId ?? "").trim();
    const rid = String(room.id ?? "").trim();
    return (
        (mid !== "" && bullionLobbyMatchIdsEqual(mid, want)) ||
        (rid !== "" && bullionLobbyMatchIdsEqual(rid, want)) ||
        (rid !== "" && bullionLobbyMatchIdsEqual(rid, `bullion-${want}`))
    );
}

/** Unfiltered lobby/trader row for a fixture id (before `selectAndOrderBullionLobbyMatches`). */
export function findRawMatchInBullionLobbyRows(rows: unknown[], matchQuery: string): unknown | null {
    const want = normalizeBullionMatchQueryParam(matchQuery);
    if (!want) return null;
    for (const row of rows) {
        const id = String((row as { id?: unknown })?.id ?? "").trim();
        if (id && bullionLobbyMatchIdsEqual(id, want)) return row;
    }
    return null;
}

export function buildBullionRoomFromLobbyRawRow(row: unknown): BullionRoom | null {
    if (!row || typeof row !== "object") return null;
    try {
        const built = buildBullionRooms([row as never], 1);
        return built[0] ?? null;
    } catch {
        return null;
    }
}

export function findBullionLobbyGamesForMatchId(
    matchId: string,
    byMatch: Record<string, BullionLobbyUserActiveGame[]>
): BullionLobbyUserActiveGame[] {
    const want = normalizeBullionMatchQueryParam(matchId);
    if (!want) return [];
    for (const [key, games] of Object.entries(byMatch)) {
        if (normalizeBullionMatchQueryParam(key) === want) return games;
    }
    return [];
}

/** Merge match-landing `enteredGames` into the my-games-by-match map (deduped by gameId). */
export function mergeBullionEnteredGamesIntoByMatchId(
    prev: Record<string, BullionLobbyUserActiveGame[]>,
    matchId: string,
    incoming: BullionLobbyUserActiveGame[]
): Record<string, BullionLobbyUserActiveGame[]> {
    if (incoming.length === 0) return prev;
    const want = normalizeBullionMatchQueryParam(matchId);
    if (!want) return prev;
    const existingKey =
        Object.keys(prev).find((k) => normalizeBullionMatchQueryParam(k) === want) ?? matchId.trim();
    const byId = new Map<number, BullionLobbyUserActiveGame>();
    for (const g of findBullionLobbyGamesForMatchId(matchId, prev)) {
        byId.set(Number(g.gameId), g);
    }
    for (const g of incoming) {
        byId.set(Number(g.gameId), g);
    }
    return { ...prev, [existingKey]: Array.from(byId.values()) };
}

/** Bullion `/bullion/my-games` can authenticate via JWT (header/cookie) or `session_token` query. */
export function hasBullionMyGamesAuth(): boolean {
    return Boolean(Cookies.get("c8_session_token") || getJwt());
}

/** Same-session requests without JWT must pass `session_token` for `jwtOptionalAuth`. */
export function withSessionTokenQuery(url: string): string {
    const st = Cookies.get("c8_session_token");
    if (!st) return url;
    const u = new URL(url);
    if (!u.searchParams.has("session_token")) u.searchParams.set("session_token", st);
    return u.toString();
}

export function buildBullionLobbyMergedRowsFromApiMatches(matches: unknown[]): unknown[] {
    const events = (Array.isArray(matches) ? matches : []).map((match: any) => ({
        ...match,
        sport: "cricket",
    }));
    return mergeBullionLobbySourceLists(events as never[], []);
}

async function enrichLobbyRowWithFrozenOddsIfNeeded(row: unknown): Promise<unknown> {
    if (!row || typeof row !== "object") return row;
    const mid = String((row as { id?: unknown })?.id ?? "").trim();
    if (!mid) return row;
    const hasFrozen =
        bullionOddsSnapshotMarkets((row as { opening_odds?: unknown }).opening_odds).length > 0 ||
        bullionOddsSnapshotMarkets((row as { markets?: unknown }).markets).length > 0;
    if (hasFrozen) return row;
    const frozen = await fetchBullionFixtureFrozenOdds(mid);
    if (!frozen.ready && !(frozen.markets?.length ?? 0)) return row;
    return mergeBullionFrozenOddsOntoLobbyRow(row, frozen);
}

/**
 * Resolve one fixture row for match landing — session/lobby peek, then Trader cricket events (not full Bullion lobby list).
 */
export async function fetchBullionLandingFixtureRowsForMatch(matchQuery: string): Promise<unknown[]> {
    const want = normalizeBullionMatchQueryParam(matchQuery);
    if (!want) return [];

    const sessionRows = readCachedBullionLobbyEvents();
    if (sessionRows?.length) {
        const hit = findRawMatchInBullionLobbyRows(sessionRows, want);
        if (hit) return [await enrichLobbyRowWithFrozenOddsIfNeeded(hit)];
    }

    const peek = peekBullionLobbyMatchesCache();
    const peekMatches = peek && Array.isArray(peek.matches) ? peek.matches : [];
    if (peekMatches.length) {
        const hit = findRawMatchInBullionLobbyRows(peekMatches, want);
        if (hit) return [await enrichLobbyRowWithFrozenOddsIfNeeded(hit)];
    }

    const trader = await fetchTraderCricketEventsCached();
    const row = trader.find((m) => bullionLobbyMatchIdsEqual(String((m as { id?: unknown })?.id ?? ""), want));
    if (!row) return [];
    return [await enrichLobbyRowWithFrozenOddsIfNeeded(row)];
}

export function getInitialBullionMatchLandingLobbyState(matchQuery?: string): {
    rows: unknown[];
    hasWarmStart: boolean;
} {
    const want = matchQuery ? normalizeBullionMatchQueryParam(matchQuery) : "";

    const sessionRows = readCachedBullionLobbyEvents();
    if (sessionRows && sessionRows.length > 0) {
        if (want) {
            const hit = findRawMatchInBullionLobbyRows(sessionRows, want);
            if (hit) return { rows: [hit], hasWarmStart: true };
            /** Do not hydrate another fixture from session when the URL targets a different match. */
            return { rows: [], hasWarmStart: false };
        }
        return { rows: sessionRows, hasWarmStart: true };
    }
    const peek = peekBullionLobbyMatchesCache();
    const peekMatches = peek && Array.isArray(peek.matches) ? peek.matches : [];
    if (peekMatches.length > 0) {
        const merged = buildBullionLobbyMergedRowsFromApiMatches(peekMatches);
        if (want) {
            const hit = findRawMatchInBullionLobbyRows(merged, want);
            if (hit) return { rows: [hit], hasWarmStart: true };
            return { rows: [], hasWarmStart: false };
        }
        return { rows: merged, hasWarmStart: true };
    }
    return { rows: [], hasWarmStart: false };
}

export type WriteBullionLandingFixtureRowOpts = {
    activeByMatch?: Record<string, BullionLobbyUserActiveGame[]>;
    /** When navigation or landing already knows the fixture is over, stamp the raw row before cache write. */
    stampFinishedFromRoom?: Pick<BullionRoom, "fixturePhase" | "status" | "isLive" | "startsAt"> | null;
};

/** Upsert one fixture into the lobby session list (do not replace the full lobby with a single row). */
export function writeBullionLandingFixtureRowToSessionCache(
    row: unknown,
    opts?: WriteBullionLandingFixtureRowOpts
): void {
    if (!row || typeof row !== "object") return;
    const id = String((row as { id?: unknown }).id ?? "").trim();
    if (!id) return;

    let patch = row as RawMatch;
    if (opts?.stampFinishedFromRoom) {
        const resolved = resolveBullionMatchFixtureState(opts.stampFinishedFromRoom, {
            useContestSignals: false,
        });
        if (resolved.matchFinished) {
            patch = stampFinishedOntoRawMatchRow(patch);
        }
    }

    const existing = readCachedBullionLobbyEvents() ?? [];
    const rest = existing.filter(
        (m) => !bullionLobbyMatchIdsEqual(String((m as { id?: unknown })?.id ?? ""), id)
    );
    const merged = mergeBullionLobbySourceLists([patch as never], rest as never[]);
    const activeByMatch = opts?.activeByMatch ?? peekBullionMyGamesByMatchIdCache() ?? undefined;
    writeCachedBullionLobbyEvents(merged, { activeByMatch });
}
